import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Car, AlertTriangle, Trash2, RotateCcw } from "lucide-react";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useTradeIns, TradeInFilters, filterTradeIns, TRADEIN_STATUSES, tradeInStatus, dateFR, UnsignedBadge } from "@/components/ImportTradeInDialog";
import { euro } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const daysSince = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
const isLate = (r) => tradeInStatus(r.t) === "Encore chez le client" && r.date && daysSince(r.date) > 30;

const StatusSelect = ({ row, onChange }) => (
  <Select value={tradeInStatus(row.t)} onValueChange={(v) => onChange(row, v)}>
    <SelectTrigger className={`h-8 w-48 text-xs ${tradeInStatus(row.t) === TRADEIN_STATUSES[1] ? "border-emerald-500 text-emerald-700" : "border-amber-500 text-amber-700"}`} data-testid={`tradein-status-${row.key}`}><SelectValue /></SelectTrigger>
    <SelectContent>{TRADEIN_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
  </Select>
);

export default function TradeIns() {
  const nav = useNavigate();
  const [allRows, setRows] = useTradeIns(true, true);
  const [linked, setLinked] = useState(new Map());
  const rows = useMemo(() => allRows && allRows.filter((r) => r.signed || linked.has(r.key)), [allRows, linked]);
  const [showHidden, setShowHidden] = useState(false);
  const hiddenCount = useMemo(() => (rows || []).filter((r) => r.t.hidden_from_list).length, [rows]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [places, setPlaces] = useState("all");
  const [status, setStatus] = useState("all");
  const [seller, setSeller] = useState("all");
  const [place, setPlace] = useState("all");
  const placesList = useMemo(() => [...new Set((rows || []).map((r) => r.place).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr")), [rows]);
  const sellers = useMemo(() => [...new Set((rows || []).map((r) => r.seller).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr")), [rows]);
  const { user } = useAuth();
  const canFilterSeller = user?.role === "admin" || !!user?.sales_manager;
  const list = useMemo(() => filterTradeIns(rows, { q, type, places, status, seller }).filter((r) => (place === "all" || r.place === place) && !!r.t.hidden_from_list === showHidden), [rows, q, type, places, status, seller, place, showHidden]);
  const [lateOnly, setLateOnly] = useState(false);
  const lateCount = useMemo(() => (rows || []).filter((r) => !r.t.hidden_from_list && isLate(r)).length, [rows]);
  const shown = lateOnly ? list.filter(isLate) : list;
  const [propose, setPropose] = useState(null);
  useEffect(() => {
    api.get("/expos").then(({ data }) => setLinked(new Map(data.filter((e) => e.used?.source_tradein).map((e) => [`${e.used.source_tradein.quote_id}-${e.used.source_tradein.index}`, e.poster?.total_salon_ttc])))).catch(() => {});
  }, []);
  const goPoster = (r) => nav(`/configurer?range=occasion&reprise=${r.quoteId}:${r.index}`);
  const changeStatus = async (row, v) => {
    try {
      await api.put(`/quotes/${row.quoteId}/trade-ins/${row.index}/status`, { status: v });
      setRows((rs) => rs.map((r) => (r.key === row.key ? { ...r, t: { ...r.t, status: v } } : r)));
      toast.success("Statut mis à jour");
      if (v === "Reprise effectuée" && !linked.has(row.key)) setPropose(row);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const setHidden = (row, v) => setRows((rs) => rs.map((r) => (r.key === row.key ? { ...r, t: { ...r.t, hidden_from_list: v } } : r)));
  const removeRow = async (row) => {
    try {
      await api.delete(`/quotes/${row.quoteId}/trade-ins/${row.index}/list`);
      setHidden(row, true);
      toast.success("Reprise retirée de la liste (le devis n'est pas modifié)");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const restoreRow = async (row) => {
    try {
      await api.put(`/quotes/${row.quoteId}/trade-ins/${row.index}/list`);
      setHidden(row, false);
      toast.success("Reprise remise dans la liste");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="tradeins-page">
      <h1 className="font-display text-3xl sm:text-4xl font-bold">Mes reprises de véhicules d'occasion</h1>
      <p className="text-sm text-muted-foreground mt-1">{canFilterSeller ? "Toutes les reprises des commandes signées (ou déjà mises en stock) de vos revendeurs. Filtrez par revendeur et suivez si le véhicule a été repris ou s'il est encore chez le client." : "Toutes les reprises des commandes signées (ou déjà mises en stock). Suivez si le véhicule a été repris ou s'il est encore chez le client."}</p>
      <div className="mt-6">
        <TradeInFilters q={q} setQ={setQ} type={type} setType={setType} places={places} setPlaces={setPlaces}>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-48" data-testid="tradein-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous les statuts</SelectItem>{TRADEIN_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          {(canFilterSeller || sellers.length > 1) && (
            <Select value={seller} onValueChange={setSeller}>
              <SelectTrigger className="w-52" data-testid="tradein-filter-seller"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tous les revendeurs</SelectItem>{sellers.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {placesList.length > 0 && (
            <Select value={place} onValueChange={setPlace}>
              <SelectTrigger className="w-60" data-testid="tradein-filter-place"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tous les lieux de stockage</SelectItem>{placesList.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </TradeInFilters>
      </div>
      {lateCount > 0 && (
        <button type="button" onClick={() => setLateOnly((v) => !v)} aria-pressed={lateOnly}
          className={`mt-4 w-full flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-left transition-colors ${lateOnly ? "border-destructive bg-destructive/10 text-destructive" : "border-destructive/40 bg-destructive/5 text-destructive hover:bg-destructive/10"}`}
          data-testid="tradein-late-alert">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span><b data-testid="tradein-late-count">{lateCount}</b> reprise{lateCount > 1 ? "s" : ""} encore chez le client depuis plus de 30 jours</span>
          <span className="ml-auto text-xs underline">{lateOnly ? "Tout afficher" : "Afficher uniquement"}</span>
        </button>
      )}
      {(hiddenCount > 0 || showHidden) && (
        <button type="button" onClick={() => setShowHidden((v) => !v)} aria-pressed={showHidden}
          className={`mt-3 inline-flex items-center gap-1.5 text-xs underline ${showHidden ? "text-[#9E7B41] font-semibold" : "text-muted-foreground hover:text-foreground"}`}
          data-testid="tradein-toggle-hidden">
          <RotateCcw className="w-3.5 h-3.5" />
          {showHidden ? "Revenir à la liste des reprises" : `Voir les reprises retirées (${hiddenCount})`}
        </button>
      )}
      {!rows ? <Loader2 className="w-6 h-6 animate-spin text-muted-foreground mx-auto my-12" /> : !shown.length ? (
        <p className="text-sm text-muted-foreground py-12 text-center" data-testid="tradeins-empty">{showHidden ? "Aucune reprise retirée." : "Aucune reprise."}</p>
      ) : (
        <div className="mt-4 rounded-xl border border-border bg-card divide-y divide-border">
          {shown.map((r) => (
            <div key={r.key} className="flex flex-wrap items-center gap-3 p-3" data-testid={`tradein-row-${r.key}`}>
              {r.t.photos?.[0]?.url ? <img src={r.t.photos[0].url} alt="" className="w-20 h-16 object-cover rounded border border-border" /> : <div className="w-20 h-16 rounded bg-muted" />}
              <div className="min-w-[220px] flex-1 text-sm">
                <div className="font-semibold">{[r.t.marque, r.t.modele].filter(Boolean).join(" ")} <Badge variant="outline" className="ml-1 text-[10px]">{r.t.type_label || r.t.type}</Badge>
                  {isLate(r) && <Badge className="ml-1 bg-destructive/10 text-destructive hover:bg-destructive/10 text-[10px]" data-testid={`tradein-late-${r.key}`}>Chez le client depuis {daysSince(r.date)} jours</Badge>}</div>
                <div className="text-xs text-muted-foreground">{[r.t.places, r.t.annee, r.t.immat && `Immat. ${r.t.immat}`].filter(Boolean).join(" · ")}</div>
                <div className="text-xs text-muted-foreground">{r.quote} · {r.client || "—"} · {dateFR(r.date)}{r.seller ? ` · ${r.seller}` : ""}{r.place ? ` · ${r.place}` : ""}</div>
                {!r.signed && <UnsignedBadge testid={`tradein-unsigned-${r.key}`} />}
              </div>
              <div className="text-sm font-mono text-[#9E7B41]" data-testid={`tradein-amount-${r.key}`}>{euro(r.t.montant_ttc || 0)}{r.t.tva_recuperable ? " TTC" : ""}</div>
              <div className="w-32 text-right text-sm" data-testid={`tradein-sale-price-${r.key}`}>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Prix de vente</div>
                <div className="font-mono font-semibold">{linked.get(r.key) != null ? (Number(linked.get(r.key)) > 0 ? `${euro(linked.get(r.key))} TTC` : <span className="text-amber-700">Prix à définir</span>) : "—"}</div>
              </div>
              <StatusSelect row={r} onChange={changeStatus} />
              {linked.has(r.key) && <Badge className="bg-[#9E7B41]/15 text-[#9E7B41] hover:bg-[#9E7B41]/15 text-[10px]" data-testid={`tradein-poster-done-${r.key}`}>Fiche de prix créée</Badge>}
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => goPoster(r)} data-testid={`tradein-to-used-${r.key}`}>
                <Car className="w-3.5 h-3.5" /> Devis occasion
              </Button>
              {showHidden ? (
                <Button size="sm" className="gap-1.5" onClick={() => restoreRow(r)} data-testid={`tradein-restore-${r.key}`}>
                  <RotateCcw className="w-3.5 h-3.5" /> Remettre dans la liste
                </Button>
              ) : (
              <ConfirmDelete label="cette reprise de la liste" name={`${[r.t.marque, r.t.modele].filter(Boolean).join(" ")} · ${r.quote} (le devis et son prix ne sont pas modifiés)`} onConfirm={() => removeRow(r)} testid={`tradein-delete-${r.key}`}>
                <Button size="icon" variant="outline" className="h-8 w-8 text-destructive" title="Supprimer de la liste" data-testid={`tradein-delete-${r.key}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </ConfirmDelete>
              )}
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!propose} onOpenChange={(v) => { if (!v) setPropose(null); }}>
        <DialogContent className="max-w-md" data-testid="tradein-propose-dialog">
          <DialogHeader>
            <DialogTitle>Créer la fiche de prix ?</DialogTitle>
            <DialogDescription>
              La reprise {propose && [propose.t.marque, propose.t.modele].filter(Boolean).join(" ")} ({propose?.quote}) est effectuée et n'a pas encore de fiche de prix. Voulez-vous la créer maintenant pour la mettre en stock ?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPropose(null)} data-testid="tradein-propose-later">Plus tard</Button>
            <Button onClick={() => { const r = propose; setPropose(null); goPoster(r); }} data-testid="tradein-propose-create">Créer la fiche de prix</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
